/**
 * The Community List (CORD-02 §8): a member's memberships, self-encrypted
 * (NIP-44) into addressable kind 33302 fragments. Any Concord client holding
 * the same npub folds the same List — this is what makes a membership created
 * here show up in Armada, and vice versa.
 */

import type { NostrEvent } from "@nostrify/nostrify";

import { KIND_COMMUNITY_LIST } from "./config";
import { bytesToB64url, b64urlToBytes, bytesToHex, hexToBytes } from "./crypto";
import type { ChannelGrant, JoinMaterial, Membership } from "./types";
import type { GiftSigner } from "./stream";

/** Minimal pool shape we need (satisfied by Nostrify's nostr / relay group). */
export interface EventSink {
  event(event: NostrEvent, opts?: { signal?: AbortSignal }): Promise<unknown>;
}

interface WireFragment {
  frags?: number;
  entries?: WireEntry[];
  tombstones?: { community_id?: string; removed_at?: number }[];
  [unknownField: string]: unknown; // round-tripped verbatim (CORD-02 §8)
}

interface WireEntry {
  community_id?: string;
  seed?: Record<string, unknown>;
  current?: Record<string, unknown>;
  added_at?: number;
  [unknownField: string]: unknown;
}

function materialToWire(m: JoinMaterial): Record<string, unknown> {
  const wire: Record<string, unknown> = {
    owner: bytesToB64url(hexToBytes(m.owner)),
    owner_salt: bytesToB64url(hexToBytes(m.owner_salt)),
    community_root: bytesToB64url(hexToBytes(m.community_root)),
    root_epoch: m.root_epoch,
    control_pk: bytesToB64url(hexToBytes(m.control_pk)),
    channels: m.channels.map(channelToWire),
    relays: m.relays,
    name: m.name,
  };
  if (m.control_root) {
    wire.control_root = bytesToB64url(hexToBytes(m.control_root));
  }
  return wire;
}

function channelToWire(c: ChannelGrant): Record<string, unknown> {
  const wire: Record<string, unknown> = { id: bytesToB64url(hexToBytes(c.id)) };
  if (c.key) wire.key = bytesToB64url(hexToBytes(c.key));
  if (c.epoch !== undefined) wire.epoch = c.epoch;
  if (c.name) wire.name = c.name;
  return wire;
}

function b64ToHex(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  try {
    return bytesToHex(b64urlToBytes(v));
  } catch {
    return undefined;
  }
}

function wireToMaterial(w: Record<string, unknown>): JoinMaterial | null {
  const owner = b64ToHex(w.owner);
  const owner_salt = b64ToHex(w.owner_salt);
  const community_root = b64ToHex(w.community_root);
  const control_pk = b64ToHex(w.control_pk);
  const root_epoch = Number(w.root_epoch ?? 0);
  if (!owner || !owner_salt || !community_root || !control_pk) return null;
  if (!Number.isInteger(root_epoch) || root_epoch < 0) return null;
  const channels: ChannelGrant[] = [];
  if (Array.isArray(w.channels)) {
    for (const c of w.channels.slice(0, 256)) {
      if (!c || typeof c !== "object") continue;
      const rec = c as Record<string, unknown>;
      const id = b64ToHex(rec.id);
      if (!id) continue;
      channels.push({
        id,
        key: b64ToHex(rec.key),
        epoch:
          rec.epoch === undefined
            ? undefined
            : Number.isInteger(Number(rec.epoch))
              ? Number(rec.epoch)
              : undefined,
        name: typeof rec.name === "string" ? rec.name : undefined,
      });
    }
  }
  return {
    owner,
    owner_salt,
    community_root,
    root_epoch,
    control_pk,
    channels,
    relays: Array.isArray(w.relays)
      ? (w.relays.filter((r) => typeof r === "string") as string[]).slice(0, 8)
      : [],
    name: typeof w.name === "string" ? w.name : "",
    control_root: b64ToHex(w.control_root),
  };
}

export interface CommunityListState {
  memberships: Membership[];
  /** Newest fragment event per index, for read-modify-write. */
  fragments: Map<number, { event: NostrEvent; json: WireFragment }>;
}

/** Fold the newest copy of every fragment into live memberships. */
export function foldCommunityList(
  fragments: Map<number, { event: NostrEvent; json: WireFragment }>,
): CommunityListState {
  const entries = new Map<string, { wire: WireEntry; addedAt: number }>();
  const tombstones = new Map<string, number>();

  for (const { json } of fragments.values()) {
    for (const t of json.tombstones ?? []) {
      const id = b64ToHex(t.community_id);
      const at = Number(t.removed_at ?? 0);
      if (!id || !Number.isFinite(at)) continue;
      tombstones.set(id, Math.max(tombstones.get(id) ?? 0, at));
    }
    for (const entry of json.entries ?? []) {
      const id = b64ToHex(entry.community_id);
      const addedAt = Number(entry.added_at ?? 0);
      if (!id || !Number.isFinite(addedAt)) continue;
      const cur = entries.get(id);
      if (!cur || addedAt > cur.addedAt) entries.set(id, { wire: entry, addedAt });
    }
  }

  const memberships: Membership[] = [];
  for (const [communityId, { wire, addedAt }] of entries) {
    // A tombstone at or after added_at retires the entry (CORD-02 §8).
    if ((tombstones.get(communityId) ?? 0) >= addedAt) continue;
    const currentWire = wire.current;
    if (!currentWire || typeof currentWire !== "object") continue;
    const current = wireToMaterial(currentWire);
    if (!current) continue;
    memberships.push({ communityId, current, addedAt });
  }
  return { memberships, fragments };
}

/**
 * Read and decrypt the member's Community List fragments. Returns null when
 * the signer cannot do NIP-44 self-encryption.
 */
export async function decryptCommunityList(
  events: NostrEvent[],
  signer: GiftSigner,
  pubkey: string,
): Promise<CommunityListState> {
  const fragments = new Map<number, { event: NostrEvent; json: WireFragment }>();
  if (!signer.nip44) return { memberships: [], fragments };

  // Newest event per fragment index.
  const byIndex = new Map<number, NostrEvent>();
  for (const e of events) {
    const d = Number(e.tags.find(([n]) => n === "d")?.[1] ?? "0");
    if (!Number.isInteger(d) || d < 0) continue;
    const cur = byIndex.get(d);
    if (!cur || e.created_at > cur.created_at) byIndex.set(d, e);
  }

  for (const [index, event] of byIndex) {
    try {
      const plain = await signer.nip44.decrypt(pubkey, event.content);
      const json = JSON.parse(plain) as WireFragment;
      fragments.set(index, { event, json });
    } catch {
      // Undecryptable or malformed fragment: means "unseen", never "empty".
    }
  }
  return foldCommunityList(fragments);
}

/**
 * Upsert one membership into the List (read-modify-write of the fragment that
 * holds it, fragment 0 for new entries), then publish. Unknown fields are
 * round-tripped verbatim so other clients' data survives (CORD-02 §8).
 */
export async function writeMembership(
  opts: {
    signer: GiftSigner;
    pubkey: string;
    membership: Membership;
    state: CommunityListState;
  },
  sink: EventSink,
): Promise<void> {
  const { signer, pubkey, membership, state } = opts;
  if (!signer.nip44) throw new Error("Signer does not support NIP-44");

  // Find the fragment already holding this entry, else use fragment 0.
  let target = 0;
  const idB64 = bytesToB64url(hexToBytes(membership.communityId));
  for (const [index, { json }] of state.fragments) {
    if ((json.entries ?? []).some((e) => e.community_id === idB64)) {
      target = index;
      break;
    }
  }

  const existing = state.fragments.get(target);
  const prevJson: WireFragment = existing?.json ?? {};
  const frags = Math.max(1, ...[...state.fragments.values()].map((f) => Number(f.json.frags ?? 1)));

  const newEntry: WireEntry = {
    community_id: idB64,
    current: materialToWire(membership.current),
    added_at: membership.addedAt,
  };
  // Round-trip unknown fields of an existing entry for this community.
  const prevEntry = (prevJson.entries ?? []).find((e) => e.community_id === idB64);
  if (prevEntry) {
    for (const [k, v] of Object.entries(prevEntry)) {
      if (!(k in newEntry)) newEntry[k] = v;
    }
  }

  const entries = (prevJson.entries ?? []).filter((e) => e.community_id !== idB64);
  entries.push(newEntry);

  const fragment: WireFragment = {
    ...prevJson,
    frags,
    entries,
    tombstones: prevJson.tombstones ?? [],
  };

  const plaintext = JSON.stringify(fragment);
  if (plaintext.length > 56 * 1024) {
    throw new Error("Community List fragment is full; cannot add membership");
  }

  const content = await signer.nip44.encrypt(pubkey, plaintext);
  // Relays resolve addressable events on created_at alone: must strictly climb.
  const created_at = Math.max(
    Math.floor(Date.now() / 1000),
    (existing?.event.created_at ?? 0) + 1,
  );
  const event = await signer.signEvent({
    kind: KIND_COMMUNITY_LIST,
    content,
    tags: [["d", String(target)]],
    created_at,
  });
  await sink.event(event);
}
